import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths } from 'date-fns';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  Download
} from 'lucide-react';
import { projectsApi, kpiApi, KpiMember } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';

export function KpiPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { hapticFeedback, showAlert } = useTelegram();

  const getDefaultRange = () => ({
    startDate: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  const loadDateRangeFromStorage = (projectId: string | undefined) => {
    if (typeof window === 'undefined' || !projectId) {
      return getDefaultRange();
    }

    try {
      const saved = window.localStorage.getItem(`kpi_period_${projectId}`);
      if (!saved) return getDefaultRange();

      const parsed = JSON.parse(saved) as { startDate?: string; endDate?: string };
      if (!parsed.startDate || !parsed.endDate) return getDefaultRange();

      return {
        startDate: parsed.startDate,
        endDate: parsed.endDate,
      };
    } catch {
      return getDefaultRange();
    }
  };

  const [dateRange, setDateRange] = useState(() => loadDateRangeFromStorage(projectId));

  // Обновляем период при изменении projectId
  useEffect(() => {
    if (projectId) {
      const savedRange = loadDateRangeFromStorage(projectId);
      setDateRange(savedRange);
    }
  }, [projectId]);

  // Сохраняем выбранный период локально (по проекту),
  // чтобы при следующем открытии страницы он восстанавливался
  useEffect(() => {
    if (typeof window === 'undefined' || !projectId) return;
    try {
      window.localStorage.setItem(
        `kpi_period_${projectId}`,
        JSON.stringify(dateRange)
      );
    } catch {
      // игнорируем ошибки localStorage
    }
  }, [projectId, dateRange]);

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  const { data: kpiData, isLoading, refetch } = useQuery({
    queryKey: ['kpi', projectId, dateRange],
    queryFn: () => kpiApi.get({
      projectId: projectId!,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }),
    enabled: !!projectId,
  });

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!project?.googleSheetId) {
      showAlert('Google Sheet не настроен для этого проекта');
      return;
    }

    try {
      setIsExporting(true);
      hapticFeedback('medium');
      await kpiApi.export(projectId!, dateRange.startDate, dateRange.endDate);
      hapticFeedback('success');
      showAlert(`KPI успешно экспортирован в Google Sheets\n\nТаблица: ${project.googleSheetId}`);
    } catch (err: any) {
      hapticFeedback('error');
      const errorMsg = err?.message || 'Не удалось экспортировать KPI';
      showAlert(`Ошибка экспорта: ${errorMsg}`);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div>
        <p className="text-sm text-tg-hint">{project?.name}</p>
        <h1 className="text-2xl font-bold text-tg-text flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          KPI
        </h1>
      </div>

      {/* Фильтр по периоду */}
      <Card className="p-4">
        <p className="text-sm font-medium text-tg-text mb-3">Период</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="date"
            label="С"
            value={dateRange.startDate}
            onChange={(e) => {
              const newStartDate = e.target.value;
              setDateRange({ ...dateRange, startDate: newStartDate });
              hapticFeedback('light');
            }}
          />
          <Input
            type="date"
            label="По"
            value={dateRange.endDate}
            onChange={(e) => {
              const newEndDate = e.target.value;
              setDateRange({ ...dateRange, endDate: newEndDate });
              hapticFeedback('light');
            }}
          />
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              refetch();
              hapticFeedback('light');
            }}
          >
            Применить
          </Button>
          {project?.googleSheetId && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={isExporting}
              className="gap-1"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Экспорт...' : 'Экспорт'}
            </Button>
          )}
        </div>
      </Card>

      {/* Общая статистика */}
      {kpiData && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            icon={<CheckCircle className="w-5 h-5 text-green-500" />}
            label="Всего задач"
            value={kpiData.totals.totalTasks}
          />
          <StatCard
            icon={<TrendingUp className="w-5 h-5 text-blue-500" />}
            label="Выполнено"
            value={kpiData.totals.completed}
            subValue={kpiData.totals.totalTasks > 0 
              ? `${Math.round((kpiData.totals.completed / kpiData.totals.totalTasks) * 100)}%`
              : '0%'
            }
          />
          <StatCard
            icon={<Clock className="w-5 h-5 text-yellow-500" />}
            label="В работе"
            value={kpiData.totals.inProgress}
          />
          <StatCard
            icon={<AlertTriangle className="w-5 h-5 text-red-500" />}
            label="Просрочено"
            value={kpiData.totals.overdue}
            variant={kpiData.totals.overdue > 0 ? 'danger' : 'default'}
          />
        </div>
      )}

      {/* Таблица по сотрудникам */}
      {kpiData && kpiData.members.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-tg-bg">
            <h2 className="font-semibold text-tg-text">По ответственным</h2>
            <p className="text-xs text-tg-hint mt-1">
              Режим: {kpiData.project.kpiSplitMode === 'EACH_FULL' 
                ? 'Каждому +1 за задачу' 
                : 'Делить между ответственными'}
            </p>
          </div>

          <div className="divide-y divide-tg-bg">
            {kpiData.members.map((member) => (
              <MemberKpiRow key={member.userId} member={member} />
            ))}
          </div>
        </Card>
      )}

      {/* Пустое состояние */}
      {kpiData && kpiData.members.length === 0 && (
        <Card className="p-8 text-center">
          <BarChart3 className="w-12 h-12 mx-auto text-tg-hint mb-4" />
          <p className="text-tg-hint">Нет данных за выбранный период</p>
        </Card>
      )}
    </div>
  );
}

function StatCard({ 
  icon, 
  label, 
  value, 
  subValue,
  variant = 'default' 
}: { 
  icon: React.ReactNode;
  label: string;
  value: number;
  subValue?: string;
  variant?: 'default' | 'danger';
}) {
  return (
    <Card className={`p-4 ${variant === 'danger' && value > 0 ? 'bg-red-50' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-tg-hint">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold ${variant === 'danger' && value > 0 ? 'text-red-500' : 'text-tg-text'}`}>
          {value}
        </span>
        {subValue && (
          <span className="text-sm text-tg-hint">{subValue}</span>
        )}
      </div>
    </Card>
  );
}

function MemberKpiRow({ member }: { member: KpiMember }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 text-left hover:bg-tg-bg/50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-tg-text">
              {member.firstName} {member.lastName || ''}
            </p>
            {member.username && (
              <p className="text-xs text-tg-hint">@{member.username}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-tg-text">
              {member.completionRate}%
            </p>
            <p className="text-xs text-tg-hint">
              {member.completed}/{member.assigned}
            </p>
          </div>
        </div>

        {/* Прогресс-бар */}
        <div className="mt-3 h-2 bg-tg-bg rounded-full overflow-hidden">
          <div 
            className="h-full bg-green-500 rounded-full transition-all"
            style={{ width: `${member.completionRate}%` }}
          />
        </div>
      </button>

      {/* Детали */}
      {isExpanded && (
        <div className="px-4 pb-4 grid grid-cols-2 gap-3 text-sm animate-slide-down">
          <div className="bg-tg-bg rounded-lg p-3">
            <p className="text-tg-hint text-xs">Назначено</p>
            <p className="font-semibold text-tg-text">{member.assigned}</p>
          </div>
          <div className="bg-tg-bg rounded-lg p-3">
            <p className="text-tg-hint text-xs">Выполнено</p>
            <p className="font-semibold text-green-500">{member.completed}</p>
          </div>
          <div className="bg-tg-bg rounded-lg p-3">
            <p className="text-tg-hint text-xs">В срок</p>
            <p className="font-semibold text-tg-text">
              {member.completedOnTime} ({member.onTimeRate}%)
            </p>
          </div>
          <div className="bg-tg-bg rounded-lg p-3">
            <p className="text-tg-hint text-xs">Просрочено</p>
            <p className={`font-semibold ${member.overdue > 0 ? 'text-red-500' : 'text-tg-text'}`}>
              {member.overdue}
            </p>
          </div>
          <div className="bg-tg-bg rounded-lg p-3">
            <p className="text-tg-hint text-xs">В работе</p>
            <p className="font-semibold text-yellow-500">{member.inProgress}</p>
          </div>
          <div className="bg-tg-bg rounded-lg p-3">
            <p className="text-tg-hint text-xs">Ср. время</p>
            <p className="font-semibold text-tg-text">
              {member.avgCompletionDays !== null ? `${member.avgCompletionDays} дн.` : '—'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

